import type { MsoListMeta } from "./types";

const MSO_LIST_RE = /mso-list:\s*l(\d+)\s+level(\d+)(?:\s+lfo(\d+))?/i;
const MSO_LEVEL_FORMAT_RE =
  /@list\s+l(\d+):level(\d+)\s*\{[^}]*mso-level-number-format:\s*([^;}"']+)/gi;

const SUPPORT_LISTS_LABEL_RE =
  /^(?:\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])\s*/i;

function parseListDefinitions(root: ParentNode): Map<string, string> {
  const defs = new Map<string, string>();
  root.querySelectorAll("style").forEach((styleEl) => {
    const text = styleEl.textContent ?? "";
    let match: RegExpExecArray | null;
    MSO_LEVEL_FORMAT_RE.lastIndex = 0;
    while ((match = MSO_LEVEL_FORMAT_RE.exec(text)) !== null) {
      const key = `l${match[1]}:level${match[2]}`;
      defs.set(key, match[3].trim().toLowerCase());
    }
  });
  return defs;
}

function readVisibleListLabel(el: HTMLElement): string | null {
  const commentWalker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_COMMENT);
  let commentNode: Node | null;
  while ((commentNode = commentWalker.nextNode())) {
    const commentText = commentNode.textContent ?? "";
    if (!/\[if\s+!supportLists\]/i.test(commentText)) continue;
    const labelMatch = commentText.match(
      /(?:^|[\s>])(\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])/i,
    );
    if (labelMatch) return labelMatch[1];
  }

  const html = el.innerHTML;
  const supportMatch = html.match(
    /<!--\s*\[if\s+!supportLists\][\s\S]*?(?:<!\[endif\]>|\[endif\]-->|<!--\[endif\]-->)/i,
  );
  if (supportMatch) {
    const temp = el.ownerDocument.createElement("div");
    temp.innerHTML = supportMatch[0].replace(
      /<!--\s*\[if\s+!supportLists\]|(?:<!\[endif\]>|\[endif\]-->|<!--\[endif\]-->)/gi,
      "",
    );
    const text = (temp.textContent ?? "").replace(/\s+/g, " ").trim();
    const labelMatch = text.match(
      /^(\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])/i,
    );
    if (labelMatch) return labelMatch[1];
  }

  const legacySupportMatch = html.match(
    /<!--\[if\s+!supportLists\]-->([\s\S]*?)\[endif\]-->/i,
  );
  if (legacySupportMatch) {
    const temp = el.ownerDocument.createElement("div");
    temp.innerHTML = legacySupportMatch[1];
    const text = (temp.textContent ?? "").replace(/\s+/g, " ").trim();
    const labelMatch = text.match(
      /^(\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])/i,
    );
    if (labelMatch) return labelMatch[1];
  }

  const leading = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  const labelMatch = leading.match(
    /^(\d+[.)]|\([a-z]+\)|\([ivxlcdm]+\)|[A-Z][.)])/i,
  );
  return labelMatch?.[1] ?? null;
}

function isListBlock(el: HTMLElement): boolean {
  const tag = el.tagName;
  return tag === "P" || tag === "LI" || /^H[1-6]$/.test(tag);
}

/**
 * Capture Word list metadata from mso-list styles before MSO cleanup strips them.
 */
export function extractMsoListMetadata(root: ParentNode): Map<HTMLElement, MsoListMeta> {
  const defs = parseListDefinitions(root);
  const result = new Map<HTMLElement, MsoListMeta>();

  root.querySelectorAll("p, li, h1, h2, h3, h4, h5, h6").forEach((node) => {
    if (!(node instanceof HTMLElement) || !isListBlock(node)) return;

    const styleAttr = node.getAttribute("style") ?? "";
    const match = styleAttr.match(MSO_LIST_RE);
    if (!match) return;

    const listId = parseInt(match[1], 10);
    const ilvl = Math.max(0, parseInt(match[2], 10) - 1);
    const lfo = match[3] ? parseInt(match[3], 10) : null;
    const defKey = `l${listId}:level${parseInt(match[2], 10)}`;
    const numFmt = defs.get(defKey) ?? null;
    const visibleLabel = readVisibleListLabel(node);

    result.set(node, { listId, ilvl, lfo, numFmt, visibleLabel });
  });

  return result;
}

export function stripWordListMarkerText(text: string): string {
  return text.replace(SUPPORT_LISTS_LABEL_RE, "").trim();
}
